import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Switch,
    FormControlLabel,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { IProduto } from '../interfaces';
import api from '../services/api';

interface ProductEditProps {
    open: boolean;
    onClose: () => void;
    product: IProduto | null;
    onSave: () => void;
}

const ProductEdit: React.FC<ProductEditProps> = ({ open, onClose, product, onSave }) => {
    const [formProduct, setFormProduct] = useState<IProduto>({
        _id: '',
        name: '',
        description: '',
        preco: 0,
        ativo: true,
        createdAt: '',
        updatedAt: '',
        modulos: [],
        tiers: [], // Added tiers property
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (product) {
            // Normaliza módulos vindos do backend (aceita nome/descricao ou name/description)
            const normalizedModulos = (product.modulos || []).map((m: any) => ({
                name: m.name || m.nome || '',
                description: m.description || m.descricao || ''
            }));

            setFormProduct({
                ...product,
                modulos: normalizedModulos,
                tiers: product.tiers || []
            } as IProduto);
        }
    }, [product]);

    const handleAddModulo = () => {
        setFormProduct({
            ...formProduct,
            modulos: [...formProduct.modulos, { name: '', description: '' }]
        });
    };

    const handleRemoveModulo = (index: number) => {
        const updatedModulos = formProduct.modulos.filter((_, i) => i !== index);
        setFormProduct({ ...formProduct, modulos: updatedModulos });
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            if (formProduct._id) {
                // Envia modulos padronizados como name/description e inclui tiers
                await api.put(`/api/products/${formProduct._id}`, {
                    ...formProduct,
                    modulos: (formProduct.modulos || []).map((modulo) => ({
                        name: modulo.name,
                        description: modulo.description
                    })),
                    tiers: formProduct.tiers || []
                });
            }
            onSave();
            onClose();
        } catch (error) {
            console.error('Erro ao salvar produto:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Editar Produto</DialogTitle>
            <DialogContent>
                <TextField
                    label="Nome"
                    value={formProduct.name}
                    onChange={(e) => setFormProduct({ ...formProduct, name: e.target.value })}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Descrição"
                    value={formProduct.description}
                    onChange={(e) => setFormProduct({ ...formProduct, description: e.target.value })}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Preço"
                    type="number"
                    value={formProduct.preco}
                    onChange={(e) => setFormProduct({ ...formProduct, preco: parseFloat(e.target.value) })}
                    fullWidth
                    margin="normal"
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={formProduct.ativo}
                            onChange={(e) => setFormProduct({ ...formProduct, ativo: e.target.checked })}
                        />
                    }
                    label="Ativo"
                />
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Módulos
                    </Typography>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>Descrição</TableCell>
                                <TableCell>Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {formProduct.modulos.map((modulo, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <TextField
                                            value={modulo.name}
                                            onChange={(e) => {
                                                const updatedModulos = [...formProduct.modulos];
                                                updatedModulos[index].name = e.target.value;
                                                setFormProduct({ ...formProduct, modulos: updatedModulos });
                                            }}
                                            fullWidth
                                            placeholder="Nome do Módulo"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            value={modulo.description}
                                            onChange={(e) => {
                                                const updatedModulos = [...formProduct.modulos];
                                                updatedModulos[index].description = e.target.value;
                                                setFormProduct({ ...formProduct, modulos: updatedModulos });
                                            }}
                                            fullWidth
                                            placeholder="Descrição do Módulo"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => handleRemoveModulo(index)} color="error">
                                            <Delete />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={handleAddModulo}
                        sx={{ mt: 2 }}
                    >
                        Adicionar Módulo
                    </Button>
                </Box>
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Faixas de Preço
                    </Typography>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Início da Faixa</TableCell>
                                <TableCell>Fim da Faixa</TableCell>
                                <TableCell>Preço</TableCell>
                                <TableCell>Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {formProduct.tiers?.map((tier, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <TextField
                                            value={tier.rangeStart}
                                            onChange={(e) => {
                                                const updatedTiers = [...(formProduct.tiers || [])];
                                                updatedTiers[index].rangeStart = parseInt(e.target.value, 10);
                                                setFormProduct({ ...formProduct, tiers: updatedTiers });
                                            }}
                                            fullWidth
                                            placeholder="Início da Faixa"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            value={tier.rangeEnd}
                                            onChange={(e) => {
                                                const updatedTiers = [...(formProduct.tiers || [])];
                                                updatedTiers[index].rangeEnd = parseInt(e.target.value, 10);
                                                setFormProduct({ ...formProduct, tiers: updatedTiers });
                                            }}
                                            fullWidth
                                            placeholder="Fim da Faixa"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            value={tier.price}
                                            onChange={(e) => {
                                                const updatedTiers = [...(formProduct.tiers || [])];
                                                updatedTiers[index].price = parseFloat(e.target.value);
                                                setFormProduct({ ...formProduct, tiers: updatedTiers });
                                            }}
                                            fullWidth
                                            placeholder="Preço"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            onClick={() => {
                                                const updatedTiers = formProduct.tiers?.filter((_, i) => i !== index) || [];
                                                setFormProduct({ ...formProduct, tiers: updatedTiers });
                                            }}
                                            color="error"
                                        >
                                            <Delete />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => {
                            const updatedTiers = [...(formProduct.tiers || [])];
                            updatedTiers.push({ rangeStart: 0, rangeEnd: 0, price: 0 });
                            setFormProduct({ ...formProduct, tiers: updatedTiers });
                        }}
                        sx={{ mt: 2 }}
                    >
                        Adicionar Faixa
                    </Button>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={handleSave} variant="contained" color="primary" disabled={loading}>
                    {loading ? 'Salvando...' : 'Salvar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProductEdit;